import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, CircularProgress, Typography, IconButton, Tooltip,
  Paper, Chip, Button, TextField, Divider, Collapse,
} from '@mui/material';
import ArrowBackIcon          from '@mui/icons-material/ArrowBack';
import ArrowBackIosNewIcon    from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon    from '@mui/icons-material/ArrowForwardIos';
import RateReviewIcon         from '@mui/icons-material/RateReview';
import DeleteOutlineIcon      from '@mui/icons-material/DeleteOutline';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import PictureAsPdfIcon       from '@mui/icons-material/PictureAsPdf';
import HtmlIcon               from '@mui/icons-material/Html';
import HelpOutlineIcon        from '@mui/icons-material/HelpOutline';
import ExpandMoreIcon         from '@mui/icons-material/ExpandMore';
import ExpandLessIcon         from '@mui/icons-material/ExpandLess';
import {
  getPitch, loadBackgrounds,
  getReviewComments, addReviewComment, updateReviewComment, deleteReviewComment,
} from './services/pitchService';
import SlideRenderer from './slides/SlideRenderer';
import LagbasLogo    from '../../components/LagbasLogo';
import { useAuth }   from '../../contexts/AuthContext';
import { colors, fontFamily } from '../../theme/tikabTokens';

const accent = colors.orange;

const BEAST_STATUSES = [
  { id: 'ny_kommentar',  label: 'Ny kommentar',  color: '#fff', bgcolor: '#D63384' },
  { id: 'ska_atgardas',  label: 'Ska åtgärdas',  color: '#fff', bgcolor: '#D32F2F' },
  { id: 'har_atgardats', label: 'Har åtgärdats', color: '#fff', bgcolor: '#388E3C' },
  { id: 'avvisad',       label: 'Avvisad',        color: '#fff', bgcolor: '#616161' },
];
function beastOf(id) { return BEAST_STATUSES.find(b => b.id === id) || BEAST_STATUSES[0]; }

// ── Pin (plupp) ───────────────────────────────────────────────────────────────

function ReviewPin({ comment, index, selected, onClick }) {
  const bs = beastOf(comment.beastStatus);
  return (
    <Box
      onClick={e => { e.stopPropagation(); onClick(comment); }}
      sx={{
        position: 'absolute',
        left: `${comment.x}%`,
        top:  `${comment.y}%`,
        transform: 'translate(-50%, -50%)',
        width: 28, height: 28,
        borderRadius: '50%',
        bgcolor: bs.bgcolor,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer',
        zIndex: 20,
        border: selected ? '3px solid #fff' : '2px solid rgba(255,255,255,0.8)',
        boxShadow: selected
          ? `0 0 0 2px ${bs.bgcolor}, 0 4px 14px rgba(0,0,0,0.5)`
          : '0 2px 8px rgba(0,0,0,0.4)',
        transition: 'transform 0.12s, box-shadow 0.12s',
        '&:hover': { transform: 'translate(-50%,-50%) scale(1.2)' },
      }}
    >
      <Typography sx={{ color: bs.color, fontWeight: 700, fontSize: '0.7rem', lineHeight: 1 }}>
        {index + 1}
      </Typography>
    </Box>
  );
}

// ── Ny kommentar-popover ──────────────────────────────────────────────────────

function NewCommentPopover({ x, y, onSave, onCancel }) {
  const [text,   setText]   = useState('');
  const [status, setStatus] = useState('ny_kommentar');
  // Popover är ~260px bred och ~210px hög. Håll den inom 0–100% med marginal.
  const left = Math.min(Math.max(x, 2), 62); // aldrig utanför höger kant
  const top  = y > 55 ? Math.max(2, x > 55 ? y - 48 : y - 44) : y + 4;

  return (
    <Paper
      elevation={10}
      onClick={e => e.stopPropagation()}
      sx={{ position: 'absolute', left: `${left}%`, top: `${top}%`, width: 260, p: 2, zIndex: 30, borderRadius: 2 }}
    >
      <Typography variant="caption" fontWeight={700} color="text.secondary"
        sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1 }}>
        Ny kommentar
      </Typography>
      <TextField
        autoFocus multiline rows={3} size="small" fullWidth
        placeholder="Skriv din kommentar…"
        value={text}
        onChange={e => setText(e.target.value)}
        onKeyDown={e => e.key === 'Escape' && onCancel()}
        sx={{ mb: 1.5 }}
      />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
        {BEAST_STATUSES.map(b => (
          <Chip key={b.id} label={b.label} size="small" onClick={() => setStatus(b.id)} sx={{
            height: 20, fontSize: '0.62rem', fontWeight: 700, cursor: 'pointer',
            bgcolor: status === b.id ? b.bgcolor : b.bgcolor + '22',
            color:   status === b.id ? b.color  : b.bgcolor,
            border: `1px solid ${b.bgcolor}55`,
          }} />
        ))}
      </Box>
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button size="small" onClick={onCancel} sx={{ textTransform: 'none' }}>Avbryt</Button>
        <Button size="small" variant="contained" disabled={!text.trim()}
          onClick={() => onSave({ text: text.trim(), beastStatus: status })}
          sx={{ bgcolor: accent, '&:hover': { bgcolor: '#c44d00' }, textTransform: 'none' }}>
          Spara
        </Button>
      </Box>
    </Paper>
  );
}

// ── Kommentarskort i panelen ──────────────────────────────────────────────────

function CommentCard({ comment, index, selected, onClick, onStatusChange, onDelete }) {
  const bs = beastOf(comment.beastStatus);
  return (
    <Box onClick={() => onClick(comment)} sx={{
      p: 1.5, borderRadius: 2, cursor: 'pointer', transition: 'all 0.12s',
      bgcolor: selected ? bs.bgcolor + '1a' : bs.bgcolor + '0a',
      border: `1px solid ${selected ? bs.bgcolor + '77' : bs.bgcolor + '33'}`,
      '&:hover': { bgcolor: bs.bgcolor + '22' },
    }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.75 }}>
        {/* Plupp-ikon */}
        <Box sx={{
          width: 22, height: 22, borderRadius: '50%', bgcolor: bs.bgcolor,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.1,
        }}>
          <Typography sx={{ color: bs.color, fontWeight: 700, fontSize: '0.65rem', lineHeight: 1 }}>{index + 1}</Typography>
        </Box>
        <Typography variant="body2" sx={{ flex: 1, lineHeight: 1.5, color: '#ddd' }}>{comment.text}</Typography>
        <IconButton size="small"
          onClick={e => { e.stopPropagation(); onDelete(comment.id); }}
          sx={{ mt: -0.5, mr: -0.5, color: '#555', '&:hover': { color: '#ff6b6b' } }}>
          <DeleteOutlineIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>
      {comment.slideIndex != null && (
        <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontSize: '0.65rem', color: '#777' }}>
          Slide {comment.slideIndex + 1}
        </Typography>
      )}
      {comment.author && (
        <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontSize: '0.65rem', color: '#666' }}>
          {comment.author}
        </Typography>
      )}
      {/* BEAst-statusväljare */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.25 }}>
        {BEAST_STATUSES.map(b => (
          <Chip key={b.id} label={b.label} size="small"
            onClick={e => { e.stopPropagation(); onStatusChange(comment.id, b.id); }}
            sx={{
              height: 18, fontSize: '0.6rem', fontWeight: 700, cursor: 'pointer',
              bgcolor: comment.beastStatus === b.id ? b.bgcolor : 'transparent',
              color:   comment.beastStatus === b.id ? b.color  : b.bgcolor,
              border: `1px solid ${b.bgcolor}55`,
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

// ── How-to-panel ─────────────────────────────────────────────────────────────

function HowToPanel() {
  const [open, setOpen] = useState(false);
  return (
    <Box sx={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
      <Box
        onClick={() => setOpen(o => !o)}
        sx={{
          px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1,
          cursor: 'pointer', userSelect: 'none',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' },
        }}
      >
        <HelpOutlineIcon sx={{ fontSize: 15, color: '#555' }} />
        <Typography variant="caption" sx={{ color: '#666', flex: 1 }}>Hur fungerar granskning?</Typography>
        {open
          ? <ExpandLessIcon sx={{ fontSize: 14, color: '#555' }} />
          : <ExpandMoreIcon sx={{ fontSize: 14, color: '#555' }} />
        }
      </Box>
      <Collapse in={open}>
        <Box sx={{ px: 2, pb: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {[
            {
              dot: '#D63384',
              title: '1. Aktivera granskningsläge',
              text: 'Klicka på "Granska"-knappen i headern. Markören blir ett hårkors.',
            },
            {
              dot: '#D63384',
              title: '2. Placera en plupp',
              text: 'Klicka exakt var i presentationen du vill kommentera. En popover öppnas.',
            },
            {
              dot: '#D63384',
              title: '3. Skriv och välj status',
              text: 'Skriv din kommentar och välj BEAst-status: Ny kommentar, Ska åtgärdas, Har åtgärdats eller Avvisad.',
            },
            {
              dot: '#388E3C',
              title: '4. Följ upp i listan',
              text: 'Alla pluppar syns i panelen. Klicka på en för att hoppa till rätt slide. Byt status direkt i listan när åtgärd är klar.',
            },
          ].map((item, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: item.dot, flexShrink: 0, mt: '5px' }} />
              <Box>
                <Typography variant="caption" fontWeight={700} sx={{ color: '#aaa', display: 'block', mb: 0.25 }}>
                  {item.title}
                </Typography>
                <Typography variant="caption" sx={{ color: '#666', lineHeight: 1.5, display: 'block' }}>
                  {item.text}
                </Typography>
              </Box>
            </Box>
          ))}

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', my: 0.5 }} />

          <Typography variant="caption" sx={{ color: '#444', fontStyle: 'italic', lineHeight: 1.5 }}>
            Framtida funktion: AI-redigering av HTML-presentationer — beskriv en ändring med ord
            och Claude uppdaterar filen automatiskt.
          </Typography>
        </Box>
      </Collapse>
    </Box>
  );
}

// ── Huvud ─────────────────────────────────────────────────────────────────────

export default function PitchReview() {
  const { pitchId }       = useParams();
  const navigate          = useNavigate();
  const { currentUser }   = useAuth();

  const [pitch,       setPitch]       = useState(null);
  const [backgrounds, setBackgrounds] = useState([]);
  const [comments,    setComments]    = useState([]);
  const [loading,     setLoading]     = useState(true);

  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentPage,  setCurrentPage]  = useState(0); // HTML/PDF: manuell sidräknare
  const [reviewMode,   setReviewMode]   = useState(false);
  const [pinDropMode,  setPinDropMode]  = useState(false); // HTML: overlay aktiv för att placera plupp
  const [mousePos,     setMousePos]     = useState({ x: 0, y: 0 }); // för cursor-följande label
  const [panelOpen,    setPanelOpen]    = useState(true);
  const [newComment,   setNewComment]   = useState(null); // { x, y, slideIndex }
  const [selected,     setSelected]     = useState(null); // comment id

  useEffect(() => {
    Promise.all([
      getPitch(pitchId),
      loadBackgrounds(),
      getReviewComments(pitchId),
    ]).then(([p, bgs, cmts]) => {
      setPitch(p);
      setBackgrounds(bgs);
      setComments(cmts);
    }).finally(() => setLoading(false));
  }, [pitchId]);

  const isExternal = pitch?.pitchType === 'html' || pitch?.pitchType === 'pdf';
  const visibleSlides = (pitch?.slides ?? []).filter(s => !s.hidden);
  const totalSlides   = visibleSlides.length;

  const prev     = useCallback(() => { setCurrentSlide(c => Math.max(0, c - 1)); setNewComment(null); }, []);
  const next     = useCallback(() => { setCurrentSlide(c => Math.min(totalSlides - 1, c + 1)); setNewComment(null); }, [totalSlides]);
  const prevPage = useCallback(() => { setCurrentPage(p => Math.max(0, p - 1)); setNewComment(null); }, []);
  const nextPage = useCallback(() => { setCurrentPage(p => p + 1); setNewComment(null); }, []);

  useEffect(() => {
    function onKey(e) {
      if (newComment) return;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next();
      if (e.key === 'ArrowLeft'  || e.key === 'ArrowUp')   prev();
      if (e.key === 'Escape') setNewComment(null);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, prev, newComment]);

  function handleOverlayClick(e) {
    if (newComment) { setNewComment(null); setPinDropMode(false); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left)  / rect.width)  * 100;
    const y = ((e.clientY - rect.top)   / rect.height) * 100;
    setNewComment({ x, y, slideIndex: isExternal ? currentPage : currentSlide });
    setPinDropMode(false);
    setSelected(null);
  }

  function togglePinDrop() {
    setPinDropMode(m => !m);
    setNewComment(null);
  }

  async function handleSaveComment({ text, beastStatus }) {
    if (!newComment) return;
    const id = await addReviewComment(pitchId, {
      slideIndex:  newComment.slideIndex,
      x:           newComment.x,
      y:           newComment.y,
      text, beastStatus,
      author:   currentUser?.displayName || currentUser?.email || '',
      authorId: currentUser?.uid || '',
    });
    const newEntry = {
      id,
      slideIndex: newComment.slideIndex,
      x: newComment.x,
      y: newComment.y,
      text, beastStatus,
      author: currentUser?.displayName || currentUser?.email || '',
    };
    setComments(prev => [...prev, newEntry]);
    setSelected(id);
    setNewComment(null);
  }

  async function handleStatusChange(commentId, beastStatus) {
    await updateReviewComment(pitchId, commentId, { beastStatus });
    setComments(prev => prev.map(c => c.id === commentId ? { ...c, beastStatus } : c));
  }

  async function handleDelete(commentId) {
    await deleteReviewComment(pitchId, commentId);
    setComments(prev => prev.filter(c => c.id !== commentId));
    if (selected === commentId) setSelected(null);
  }

  if (loading) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!pitch) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="text.secondary">Presentationen hittades inte.</Typography>
      </Box>
    );
  }

  const TypeIcon = pitch.pitchType === 'pdf' ? PictureAsPdfIcon : pitch.pitchType === 'html' ? HtmlIcon : null;

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#111' }}>

      {/* Cursor-följande label i pin-drop-läge (HTML/PDF) */}
      {isExternal && pinDropMode && !newComment && (
        <Box sx={{
          position: 'fixed',
          left: mousePos.x + 14,
          top:  mousePos.y - 8,
          pointerEvents: 'none',
          zIndex: 9999,
          bgcolor: '#D63384',
          color: '#fff',
          px: 1.25, py: 0.5,
          borderRadius: 1,
          fontSize: '0.75rem',
          fontFamily,
          fontWeight: 600,
          whiteSpace: 'nowrap',
          boxShadow: '0 2px 10px rgba(0,0,0,0.5)',
        }}>
          Klicka för att placera plupp
        </Box>
      )}

      {/* ── Header ── */}
      <Box sx={{
        bgcolor: '#1a1a1a', color: 'white', px: 2, py: 1,
        display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0,
        borderBottom: '1px solid rgba(255,255,255,0.08)',
      }}>
        <LagbasLogo />
        <IconButton color="inherit" size="small" onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </IconButton>

        {TypeIcon && <TypeIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.4)' }} />}
        <Typography fontWeight={700} noWrap sx={{ flex: 1, fontFamily }}>
          {pitch.title}
        </Typography>

        {/* Slide-nav + Granska gruppade (bara för PITCH) */}
        {!isExternal && totalSlides > 0 && (
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 0.5,
            border: '1px solid rgba(255,255,255,0.12)', borderRadius: 2, px: 0.5,
          }}>
            <IconButton size="small" color="inherit" disabled={currentSlide === 0} onClick={prev}>
              <ArrowBackIosNewIcon sx={{ fontSize: 13 }} />
            </IconButton>
            <Typography variant="caption" sx={{ color: '#aaa', minWidth: 44, textAlign: 'center', fontFamily }}>
              {currentSlide + 1} / {totalSlides}
            </Typography>
            <IconButton size="small" color="inherit" disabled={currentSlide >= totalSlides - 1} onClick={next}>
              <ArrowForwardIosIcon sx={{ fontSize: 13 }} />
            </IconButton>

            <Box sx={{ width: '1px', height: 20, bgcolor: 'rgba(255,255,255,0.12)', mx: 0.25 }} />

            <Button
              size="small"
              variant={reviewMode ? 'contained' : 'text'}
              startIcon={<RateReviewIcon sx={{ fontSize: '15px !important' }} />}
              onClick={() => { setReviewMode(r => !r); setNewComment(null); setPinDropMode(false); }}
              sx={{
                textTransform: 'none', fontFamily, px: 1,
                ...(reviewMode
                  ? { bgcolor: '#D63384', color: '#fff', '&:hover': { bgcolor: '#ad1a66' } }
                  : { color: 'rgba(255,255,255,0.6)', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.08)' } }
                ),
              }}
            >
              {reviewMode ? 'Granskar' : 'Granska'}
            </Button>
          </Box>
        )}

        {/* Sid-nav + Granska gruppade (för HTML/PDF) */}
        {isExternal && (
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 0.5,
            border: '1px solid rgba(255,255,255,0.12)', borderRadius: 2, px: 0.5,
          }}>
            <IconButton size="small" color="inherit" disabled={currentPage === 0} onClick={prevPage}>
              <ArrowBackIosNewIcon sx={{ fontSize: 13 }} />
            </IconButton>
            <Typography variant="caption" sx={{ color: '#aaa', minWidth: 30, textAlign: 'center', fontFamily }}>
              {currentPage + 1}
            </Typography>
            <IconButton size="small" color="inherit" onClick={nextPage}>
              <ArrowForwardIosIcon sx={{ fontSize: 13 }} />
            </IconButton>

            <Box sx={{ width: '1px', height: 20, bgcolor: 'rgba(255,255,255,0.12)', mx: 0.25 }} />

            <Button
              size="small"
              variant={reviewMode ? 'contained' : 'text'}
              startIcon={<RateReviewIcon sx={{ fontSize: '15px !important' }} />}
              onClick={() => { setReviewMode(r => !r); setNewComment(null); setPinDropMode(false); }}
              sx={{
                textTransform: 'none', fontFamily, px: 1,
                ...(reviewMode
                  ? { bgcolor: '#D63384', color: '#fff', '&:hover': { bgcolor: '#ad1a66' } }
                  : { color: 'rgba(255,255,255,0.6)', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.08)' } }
                ),
              }}
            >
              {reviewMode ? 'Granskar' : 'Granska'}
            </Button>
          </Box>
        )}

        {/* Panel-toggle */}
        <Tooltip title={panelOpen ? 'Dölj kommentarer' : 'Visa kommentarer'}>
          <IconButton color="inherit" size="small" onClick={() => setPanelOpen(o => !o)}>
            <FormatListBulletedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── Body: presentation + panel ── */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* ── Presentation ── */}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, overflow: 'auto' }}>
          {isExternal ? (
            /* HTML / PDF — iframe med overlay endast i pin-drop-läge */
            <Box
              onMouseMove={e => setMousePos({ x: e.clientX, y: e.clientY })}
              sx={{ position: 'relative', width: '100%', height: '100%' }}
            >
              <iframe
                src={pitch.externalFileUrl}
                title={pitch.title}
                style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
              />
              {/* Overlay — bara aktiv när pin-drop-läge eller popover är öppen */}
              <Box
                onClick={handleOverlayClick}
                sx={{
                  position: 'absolute', inset: 0,
                  cursor: pinDropMode ? 'crosshair' : 'default',
                  pointerEvents: pinDropMode || newComment ? 'auto' : 'none',
                  zIndex: 10,
                  bgcolor: pinDropMode ? 'rgba(214,51,132,0.04)' : 'transparent',
                  transition: 'background 0.2s',
                }}
              />
              {/* Pluppar — bara i review-läge, enbart aktuell sida (null = sida 0 för äldre pluppar) */}
              {reviewMode && comments
                .filter(c => (c.slideIndex ?? 0) === currentPage)
                .map((c, i) => (
                  <ReviewPin key={c.id} comment={c} index={i}
                    selected={selected === c.id} onClick={c2 => setSelected(c2.id)} />
                ))}
              {/* Ny kommentar-popover */}
              {newComment && (
                <NewCommentPopover x={newComment.x} y={newComment.y}
                  onSave={handleSaveComment} onCancel={() => { setNewComment(null); setPinDropMode(false); }} />
              )}
              {/* "Placera plupp"-knapp — visas bara när ej i pin-drop-läge */}
              {reviewMode && !newComment && !pinDropMode && (
                <Box sx={{ position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 15 }}>
                  <Button
                    size="small" variant="outlined"
                    onClick={togglePinDrop}
                    sx={{
                      textTransform: 'none', fontFamily, backdropFilter: 'blur(8px)',
                      color: '#fff', borderColor: 'rgba(255,255,255,0.4)',
                      bgcolor: 'rgba(0,0,0,0.5)', '&:hover': { borderColor: '#D63384', color: '#D63384' },
                    }}
                  >
                    + Placera plupp
                  </Button>
                </Box>
              )}
            </Box>
          ) : (
            /* PITCH — slide renderer med overlay */
            <Box sx={{ width: '100%', maxWidth: '900px' }}>
              <Box sx={{ position: 'relative', aspectRatio: '16/9', borderRadius: 1 }}>
                {/* Slide-innehåll med egen overflow:hidden så pluppar/popover kan sticka ut */}
                <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden', borderRadius: 1 }}>
                  {visibleSlides[currentSlide] ? (
                    <SlideRenderer
                      slide={visibleSlides[currentSlide]}
                      backgrounds={backgrounds}
                      slideTheme={pitch.slideTheme}
                      altLogoUrl={pitch.altLogoUrl}
                    />
                  ) : (
                    <Box sx={{ width: '100%', height: '100%', bgcolor: '#222' }} />
                  )}
                </Box>
                {/* Overlay */}
                <Box
                  onClick={handleOverlayClick}
                  sx={{
                    position: 'absolute', inset: 0,
                    cursor: reviewMode ? 'crosshair' : 'default',
                    pointerEvents: reviewMode || newComment ? 'auto' : 'none',
                    zIndex: 10,
                    bgcolor: reviewMode ? 'rgba(255,152,0,0.04)' : 'transparent',
                    transition: 'background 0.2s',
                  }}
                />
                {/* Pluppar för denna slide — bara i review-läge, enbart aktuell slide */}
                {reviewMode && comments
                  .filter(c => c.slideIndex === currentSlide)
                  .map((c) => {
                    const globalIndex = comments.findIndex(x => x.id === c.id);
                    return (
                      <ReviewPin key={c.id} comment={c} index={globalIndex}
                        selected={selected === c.id} onClick={c2 => setSelected(c2.id)} />
                    );
                  })}
                {/* Ny kommentar-popover */}
                {newComment && (
                  <NewCommentPopover x={newComment.x} y={newComment.y}
                    onSave={handleSaveComment} onCancel={() => setNewComment(null)} />
                )}
              </Box>
              {/* Slide-dots */}
              {totalSlides > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mt: 1.5 }}>
                  {visibleSlides.map((_, i) => {
                    const hasCmt = comments.some(c => c.slideIndex === i);
                    return (
                      <Box key={i} onClick={() => { setCurrentSlide(i); setNewComment(null); }} sx={{
                        width: i === currentSlide ? 20 : 8, height: 8, borderRadius: 4,
                        bgcolor: i === currentSlide ? accent : hasCmt ? '#D63384aa' : 'rgba(255,255,255,0.2)',
                        cursor: 'pointer', transition: 'all 0.2s',
                        border: hasCmt && i !== currentSlide ? '1px solid #D6338466' : 'none',
                      }} />
                    );
                  })}
                </Box>
              )}
            </Box>
          )}
        </Box>

        {/* ── Kommentarspanel ── */}
        {panelOpen && (
          <Box sx={{
            width: 300, flexShrink: 0,
            bgcolor: '#1a1a1a',
            borderLeft: '1px solid rgba(255,255,255,0.08)',
            display: 'flex', flexDirection: 'column',
            overflow: 'hidden',
          }}>
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography fontWeight={700} sx={{ color: '#eee', fontFamily, fontSize: '0.85rem', flex: 1 }}>
                  Granskningskommentarer
                </Typography>
                <Typography variant="caption" sx={{ color: '#555' }}>
                  {comments.length} st
                </Typography>
              </Box>
            </Box>

            {/* How-to */}
            <HowToPanel />

            <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {comments.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <RateReviewIcon sx={{ color: '#444', fontSize: 36, mb: 1 }} />
                  <Typography variant="caption" display="block" sx={{ color: '#666' }}>
                    Inga kommentarer ännu.
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mt: 0.5, color: '#555' }}>
                    Aktivera granskningsläge och klicka på presentationen.
                  </Typography>
                </Box>
              ) : (
                comments.map((c, i) => (
                  <CommentCard
                    key={c.id}
                    comment={c}
                    index={i}
                    selected={selected === c.id}
                    onClick={c2 => {
                      setSelected(c2.id);
                      if (isExternal && c2.slideIndex != null) setCurrentPage(c2.slideIndex);
                      if (!isExternal && c2.slideIndex != null) setCurrentSlide(c2.slideIndex);
                    }}
                    onStatusChange={handleStatusChange}
                    onDelete={handleDelete}
                  />
                ))
              )}
            </Box>

            {/* BEAst-legend */}
            <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
              <Divider sx={{ mb: 1.5, borderColor: 'rgba(255,255,255,0.06)' }} />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {BEAST_STATUSES.map(b => (
                  <Box key={b.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: b.bgcolor }} />
                    <Typography variant="caption" sx={{ color: '#555', fontSize: '0.62rem' }}>{b.label}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
